import { useState, useEffect, useCallback } from 'react';
import {
    ShieldCheck, RefreshCw, CheckCircle, XCircle, AlertTriangle,
    Truck, Users, FileText, Calculator, Zap, Info, MapPin
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import TlIcon from '../../components/TlIcon';
import './FleetReadinessPage.css';

const CONDITION_LABELS = {
    fleetRecord: 'Filo Kayd\u0131',
    activeCourierMember: 'Aktif Kurye \u00DCyeli\u011Fi',
    legalAgreementAcceptance: 'S\u00F6zle\u015Fme Onay\u0131',
    activeCostHistory: 'Aktif Maliyet Ge\u00E7mi\u015Fi',
    courierCostRecords: 'Kurye Maliyet Kay\u0131tlar\u0131',
    courierRegionAssignments: 'Kurye B\u00f6lge Atamalar\u0131',
};

const CONDITION_ICONS = {
    fleetRecord: Truck,
    activeCourierMember: Users,
    legalAgreementAcceptance: FileText,
    activeCostHistory: TlIcon,
    courierCostRecords: Calculator,
    courierRegionAssignments: MapPin,
};

const CONDITION_DESCRIPTIONS = {
    fleetRecord: 'Filonun sistemde aktif ve onayl\u0131 olmas\u0131 gerekir.',
    activeCourierMember: 'Filosunda en az 1 aktif ve onayl\u0131 kurye bulunmal\u0131d\u0131r.',
    legalAgreementAcceptance: 'Zorunlu yasal s\u00F6zle\u015Fmelerin tamam\u0131 kabul edilmi\u015F olmal\u0131d\u0131r.',
    activeCostHistory: 'En az 1 aktif ve ge\u00E7erli maliyet kayd\u0131 (tarife) olmal\u0131d\u0131r.',
    courierCostRecords: 'Kuryeler i\u00E7in ge\u00E7erli maliyet kay\u0131tlar\u0131 tan\u0131mlanm\u0131\u015F olmal\u0131d\u0131r.',
    courierRegionAssignments: 'Her kuryenin en az 1 b\u00F6lge atamas\u0131 olmal\u0131d\u0131r. B\u00F6lgesi olmayan kurye atama havuzuna girmez, sipari\u015F alamaz.',
};

const FleetReadinessPage = () => {
    const [readiness, setReadiness] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchReadiness = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_READINESS.CHECK);
            if (res.success) {
                const d = res.data?.data || res.data;
                if (d && d.conditions) {
                    setReadiness(d);
                } else {
                    setError('Haz\u0131rl\u0131k durumu y\u00FCklenemedi');
                }
            } else {
                setError(res.error || 'Haz\u0131rl\u0131k durumu y\u00FCklenemedi');
            }
        } catch {
            setError('Beklenmeyen bir hata olu\u015Ftu');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchReadiness();
    }, [fetchReadiness]);

    const conditions = readiness?.conditions
        ? Object.entries(readiness.conditions).map(([key, val]) => ({ key, ...val }))
        : [];

    const metCount = conditions.filter(c => c.isMet).length;
    const totalCount = conditions.length;
    const progressPercent = totalCount > 0 ? Math.round((metCount / totalCount) * 100) : 0;

    return (
        <div className="frd-page">
            <div className="page-header">
                <div className="header-left">
                    <div className="page-icon" style={{ background: 'rgba(34,197,94,0.08)', borderColor: 'rgba(34,197,94,0.2)' }}>
                        <ShieldCheck size={18} style={{ color: '#22c55e' }} />
                    </div>
                    <div className="page-title">
                        <h1>Haz\u0131rl\u0131k Durumu</h1>
                        <span className="count-badge">Sipari\u015F Alma Ko\u015Fullar\u0131</span>
                    </div>
                </div>
                <div className="header-right">
                    <button
                        className="btn btn-secondary"
                        onClick={fetchReadiness}
                        disabled={loading}
                    >
                        <RefreshCw size={16} className={loading ? 'spinning' : ''} />
                        Yenile
                    </button>
                </div>
            </div>

            {error && (
                <div className="frd-alert frd-alert-error">
                    <AlertTriangle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {loading && (
                <div className="frd-loading">
                    <RefreshCw size={20} className="spinning" />
                    <span>Kontrol ediliyor...</span>
                </div>
            )}

            {!loading && !error && readiness && (
                <>
                    <div className={`frd-summary ${readiness.isReady ? 'frd-summary-ready' : 'frd-summary-not-ready'}`}>
                        <div className="frd-summary-icon">
                            {readiness.isReady
                                ? <Zap size={28} />
                                : <AlertTriangle size={28} />
                            }
                        </div>
                        <div className="frd-summary-text">
                            <h2>{readiness.fleetName || 'Filo'}</h2>
                            <p>
                                {readiness.isReady
                                    ? 'Tamamland\u0131! T\u00FCm ko\u015Fullar sa\u011Flan\u0131yor, filonuz sipari\u015F almaya haz\u0131r.'
                                    : `${readiness.failedConditionCount} eksik ko\u015Ful var. Bu ko\u015Fullar tamamlanmadan filonuz sipari\u015F alamaz.`
                                }
                            </p>
                            <div className="frd-summary-progress">
                                <div className="frd-progress-bar">
                                    <div
                                        className="frd-progress-fill"
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                </div>
                                <span className="frd-progress-text">{metCount} / {totalCount} tamamland\u0131</span>
                            </div>
                        </div>
                        <div className={`frd-score-circle ${readiness.isReady ? 'ready' : 'not-ready'}`}>
                            <span className="frd-score-value">{progressPercent}</span>
                            <span className="frd-score-label">%</span>
                        </div>
                    </div>

                    <div className="frd-conditions">
                        <div className="frd-conditions-title">
                            <Info size={16} />
                            <span>Haz\u0131rl\u0131k Ko\u015Fullar\u0131</span>
                        </div>
                        {conditions.map((cond) => {
                            const Icon = CONDITION_ICONS[cond.key] || ShieldCheck;
                            const label = CONDITION_LABELS[cond.key] || cond.conditionName || cond.key;
                            const desc = CONDITION_DESCRIPTIONS[cond.key] || '';

                            return (
                                <div
                                    key={cond.key}
                                    className={`frd-condition-card ${cond.isMet ? 'met' : 'not-met'}`}
                                >
                                    <div className="frd-condition-header">
                                        <div className="frd-condition-left">
                                            <div className={`frd-condition-status ${cond.isMet ? 'met' : 'not-met'}`}>
                                                {cond.isMet ? <CheckCircle size={20} /> : <XCircle size={20} />}
                                            </div>
                                            <div className="frd-condition-icon-wrap">
                                                <Icon size={18} />
                                            </div>
                                            <div className="frd-condition-info">
                                                <h4>{label}</h4>
                                                <p className="frd-condition-msg">
                                                    {cond.isMet
                                                        ? <span className="frd-msg-ok">{cond.message || 'Ko\u015Ful sa\u011Flan\u0131yor'}</span>
                                                        : <span className="frd-msg-fail">{cond.message || 'Ko\u015Ful sa\u011Flanm\u0131yor'}</span>
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                        <div className={`frd-condition-badge ${cond.isMet ? 'met' : 'not-met'}`}>
                                            {cond.isMet ? 'Tamam' : 'Eksik'}
                                        </div>
                                    </div>
                                    {desc && (
                                        <div className="frd-condition-desc">
                                            <Info size={14} />
                                            <span>{desc}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {readiness.recommendations?.length > 0 && (
                        <div className="frd-recommendations">
                            <h3><AlertTriangle size={16} /> Yap\u0131lmas\u0131 Gerekenler</h3>
                            <div className="frd-rec-list">
                                {readiness.recommendations.map((rec, i) => (
                                    <div key={i} className="frd-rec-item">
                                        <span className="frd-rec-num">{i + 1}</span>
                                        <span className="frd-rec-text">{rec}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            {!loading && !error && !readiness && (
                <div className="frd-empty">
                    <ShieldCheck size={48} />
                    <p>Haz\u0131rl\u0131k durumu bulunamad\u0131</p>
                </div>
            )}
        </div>
    );
};

export default FleetReadinessPage;
